import * as React from "react";
import { ComponentProps, useMemo } from "react";
import { Link } from "react-router-dom";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { typeScale } from "lib/themeUtils";

import { Card } from "../../elements/card";
import { Grid } from "../../elements/grid";
import { Summary } from "../summary";

import iconChevronSrc from "./icon-chevron.svg";

const Element = styled.div`
  padding-left: 1rem;
  display: flex;
  align-items: center;
  ${typeScale.sm};
  gap: 1rem;
  justify-content: right;
`;

const Actions = styled(Element)`
  border-left: 1px solid ${({ theme }) => theme.colours.bordersLight};
  justify-content: center;
`;

const ItemLinkWrap = styled.div`
  background: ${(props) => props.theme.colours.blue[900]};
  color: ${(props) => props.theme.colours.white};
  border-radius: 2rem;
  display: flex;
  width: 2rem;
  height: 2rem;
  margin: 0 1rem;
  justify-content: center;
  align-items: center;
  align-self: center;
  transition: all 300ms ease-in-out;

  &:hover {
    background: ${(props) => props.theme.colours.blue[700]};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colours.focus};
  }
`;

const ItemCard = styled(Card)<{ linkTo?: unknown }>`
  &:hover ${ItemLinkWrap} {
    background: ${(props) => props.theme.colours.blue[700]};
  }
  position: relative;
`;

const ListLink = styled(Link)`
  &:hover ${ItemCard} {
    ${({ theme }) => `border-color: ${theme.colours.bordersDark}`};
  }
  text-decoration: none;
  color: inherit;
`;

const MetaContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-grow: 1;
  align-items: baseline;
  justify-self: flex-end;
  ${typeScale.sm};
  color: ${(props) => props.theme.colours.bodyLight};
  gap: 0.5rem;
  justify-content: flex-end;
  ${({ theme }) => theme.breakpoints.up("lg")} {
    display: block;
    border: none;
  }
  ${Summary.Item} {
    margin: 0;
    dt {
      width: auto;
    }
  }
`;

const TitleContainer = styled.header`
  position: relative;
  display: flex;
  flex: 2;
  align-items: baseline;
  border-bottom: 1px solid ${(props) => props.theme.colours.bordersLight};
  ${({ theme }) => theme.breakpoints.down("lg")} {
    margin: -1rem -1rem 0;
    padding: 0.5rem 1rem;
  }
  ${({ theme }) => theme.breakpoints.up("lg")} {
    flex-direction: column;
    border: none;
  }
  h3 {
    margin: 0;
  }
`;

const TagsContainer = styled.div`
  position: absolute;
  top: -1rem;
`;

const ContentGrid = styled(Grid)`
  align-self: center;
`;

const CardWrapper: React.FC<
  Omit<ComponentProps<typeof ListLink>, "to"> & {
    to: ComponentProps<typeof ListLink>["to"] | undefined;
  }
> = ({ children, to, ...props }) => {
  return isNil(to) ? (
    <>{children}</>
  ) : (
    <ListLink to={to} {...props}>
      {children}
    </ListLink>
  );
};

const titleColumns = { xs: [1], lg: [1, 4] };

const ListItem: React.FC<
  {
    title?: Maybe<React.ReactNode>;
    meta?: Maybe<React.ReactNode>;
    actions?: Maybe<React.ReactNode>;
    tags?: Maybe<React.ReactNode>;
    linkTo?: string;
    "aria-label"?: string;
  } & Omit<ComponentProps<typeof ListLink>, "to">
> = ({ tags, title, meta, actions, linkTo, children, ...props }) => {
  const itemColumns = useMemo(() => {
    const cols = [1, actions ? "minmax(max-content, 145px)" : undefined, linkTo ? "min-content" : undefined];
    return cols.filter((el) => !isNil(el)) as Array<number | string>;
  }, [actions, linkTo]);

  const showTile = title || meta;
  return (
    <CardWrapper to={linkTo} {...props}>
      <ItemCard $tight>
        {tags && <TagsContainer>{tags}</TagsContainer>}
        <Grid $cols={showTile ? titleColumns : "1fr"} $tight>
          {showTile && (
            <TitleContainer>
              <h3>{title}</h3>
              {meta && <MetaContainer>{meta}</MetaContainer>}
            </TitleContainer>
          )}
          <ContentGrid $tight $cols={itemColumns}>
            <div>{children}</div>
            {actions && <Actions>{actions}</Actions>}
            {linkTo && (
              <Element>
                <ItemLinkWrap>
                  <img src={iconChevronSrc} alt={props["aria-label"]} />
                </ItemLinkWrap>
              </Element>
            )}
          </ContentGrid>
        </Grid>
      </ItemCard>
    </CardWrapper>
  );
};

export const List: React.FC<React.ComponentProps<typeof Grid>> & { Item: typeof ListItem } = ({ ...props }) => (
  <Grid {...props} />
);
List.Item = ListItem;
